'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import AppLogo from '@/components/ui/AppLogo';
import LanguageToggle from '@/components/ui/LanguageToggle';
import { createClient } from '@/lib/supabase/client';
import { Mail, ArrowRight, AlertCircle, Loader2, Lock, Eye, EyeOff, ShoppingCart, Fuel, RefreshCw, Briefcase } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


type RoleTab = 'buyer' | 'supplier' | 'broker';
type LoginForm = { email: string; password: string };

export default function LoginContent() {
  const [lang, setLang] = useState<'en' | 'ar'>('en');
  const [activeRole, setActiveRole] = useState<RoleTab>('buyer');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [seedLoading, setSeedLoading] = useState(false);
  const [seedResult, setSeedResult] = useState<string | null>(null);
  const supabase = createClient();
  const dir = lang === 'ar' ? 'rtl' : 'ltr';

  useEffect(() => {
    const saved = typeof window !== 'undefined' ? localStorage.getItem('nautex_lang') : null;
    if (saved === 'en' || saved === 'ar') setLang(saved);
  }, []);

  const handleLangChange = (l: 'en' | 'ar') => {
    setLang(l);
    if (typeof window !== 'undefined') localStorage.setItem('nautex_lang', l);
  };

  const { register, handleSubmit, setValue, formState: { errors } } = useForm<LoginForm>();

  const onSubmit = async (data: LoginForm) => {
    setLoading(true);
    try {
      // Server-side login with role validation
      const res = await fetch('/api/auth/verify-platform-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: data.email, password: data.password, role: activeRole }),
      });
      const json = await res.json();
      if (!res.ok) {
        // Pending approval — redirect to /pending-approval page
        if (json.code === 'ACCOUNT_PENDING') {
          window.location.href = '/pending-approval';
          return;
        }
        throw new Error(json.error || 'Invalid email or password.');
      }

      const { error: sessionError } = await supabase.auth.setSession({
        access_token: json.access_token,
        refresh_token: json.refresh_token,
      });
      if (sessionError) throw sessionError;

      toast.success('Signed in successfully');

      // Redirect based on role
      const role = json.role;
      if (role === 'supplier') {
        window.location.href = '/supplier?tab=home';
      } else if (role === 'broker') {
        window.location.href = '/broker?tab=home';
      } else if (role === 'platform_admin' || role === 'super_admin') {
        window.location.href = '/admin-panel?tab=overview';
      } else {
        // buyer (default)
        window.location.href = '/buyer?tab=home';
      }
    } catch (err: any) {
      toast.error(err?.message || 'Invalid email or password');
      setLoading(false);
    }
  };

  // fillDemo removed — demo credentials are server-only env vars, not in client bundle

  // Quick-fill from NEXT_PUBLIC_SEED_* env vars — always visible for demo
  const seedEmails = {
    buyer: process.env.NEXT_PUBLIC_SEED_BUYER_EMAIL ?? 'seed.buyer@nautex.tech',
    supplier: process.env.NEXT_PUBLIC_SEED_SUPPLIER_EMAIL ?? 'seed.supplier@nautex.tech',
    broker: process.env.NEXT_PUBLIC_SEED_BROKER_EMAIL ?? 'seed.broker@nautex.tech',
  };
  const seedPasswords = {
    buyer: process.env.NEXT_PUBLIC_SEED_BUYER_PASSWORD ?? '',
    supplier: process.env.NEXT_PUBLIC_SEED_SUPPLIER_PASSWORD ?? '',
    broker: process.env.NEXT_PUBLIC_SEED_BROKER_PASSWORD ?? '',
  };

  const handleSeedAccounts = async () => {
    setSeedLoading(true);
    setSeedResult(null);
    try {
      const res = await fetch('/api/admin/seed-accounts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ secret: 'NAUTEX_SEED_TRIGGER_2026' }),
      });
      const data = await res.json();

      if (res.status === 401) {
        setSeedResult(`❌ Auth rejected: ${data.detail ?? data.error ?? 'Unauthorized'}`);
        return;
      }

      if (res.ok || res.status === 207) {
        const accountResults = Object.entries(data.results || {}).filter(([k]) => k !== 'service_zones');
        const allOk = accountResults.every(([, v]) => (v as any).success);

        if (allOk) {
          setSeedResult('✅ Accounts ready! Fill credentials above and sign in.');
        } else {
          // Build a detailed failure message showing which account failed and why
          const failures = accountResults
            .filter(([, v]) => !(v as any).success)
            .map(([email, v]: [string, any]) => {
              const lastLog = (v.log as string[] | undefined)?.slice(-1)[0] ?? v.error ?? 'unknown error';
              return `${email.split('@')[0]}: ${lastLog}`;
            });
          setSeedResult(`⚠️ Partial seed — failed:\n${failures.join('\n')}`);
        }
      } else {
        setSeedResult(`❌ Error (${res.status}): ${data.detail ?? data.error ?? 'Unknown error'}`);
      }
    } catch (err: any) {
      setSeedResult(`❌ Network error: ${err.message}`);
    } finally {
      setSeedLoading(false);
    }
  };

  const tabs: { id: RoleTab; labelEn: string; labelAr: string; icon: React.ElementType; color: string; activeBg: string }[] = [
    {
      id: 'buyer',
      labelEn: 'I am a Buyer',
      labelAr: 'أنا مشترٍ',
      icon: ShoppingCart,
      color: 'text-blue-400',
      activeBg: 'bg-blue-500/15 border-blue-500/40',
    },
    {
      id: 'supplier',
      labelEn: 'I am a Supplier',
      labelAr: 'أنا مورد',
      icon: Fuel,
      color: 'text-emerald-400',
      activeBg: 'bg-emerald-500/15 border-emerald-500/40',
    },
    {
      id: 'broker',
      labelEn: 'I am a Broker',
      labelAr: 'أنا وسيط',
      icon: Briefcase,
      color: 'text-amber-400',
      activeBg: 'bg-amber-500/15 border-amber-500/40',
    },
  ];

  const activeTab = tabs.find(t => t.id === activeRole)!;

  return (
    <div className="min-h-screen nautex-gradient-bg flex flex-col" dir={dir}>
      {/* Top bar */}
      <div className="flex items-center justify-between px-6 py-4">
        <Link href="/" className="flex items-center gap-2">
          <AppLogo size={32} />
          <span className="font-bold text-sm text-foreground tracking-tight">NautEX</span>
        </Link>
        <LanguageToggle lang={lang} onChange={handleLangChange} />
      </div>

      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          {/* Role selector tabs */}
          <div className="grid grid-cols-3 gap-3 mb-6">
            {tabs.map(tab => {
              const Icon = tab.icon;
              const isActive = activeRole === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveRole(tab.id)}
                  className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all duration-200 ${
                    isActive
                      ? `${tab.activeBg} border-opacity-100`
                      : 'border-border/40 bg-card/40 hover:border-border hover:bg-card/60'
                  }`}
                >
                  <Icon size={22} className={isActive ? tab.color : 'text-muted-foreground'} />
                  <span className={`text-sm font-semibold ${isActive ? 'text-foreground' : 'text-muted-foreground'}`}>
                    {lang === 'ar' ? tab.labelAr : tab.labelEn}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Login card */}
          <div className="card-surface shadow-2xl animate-slide-up">
            {/* Header */}
            <div className="p-6 border-b border-border">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${activeTab.activeBg}`}>
                  <activeTab.icon size={20} className={activeTab.color} />
                </div>
                <div>
                  <h1 className="text-lg font-bold text-foreground">
                    {lang === 'ar' ? 'مرحبًا بعودتك' : 'Welcome back'}
                  </h1>
                  <p className="text-xs text-muted-foreground">
                    {lang === 'ar'
                      ? `سجّل الدخول كـ${activeRole === 'buyer' ? 'مشترٍ' : activeRole === 'supplier' ? 'مورد' : 'وسيط'}`
                      : `Sign in as a ${activeRole === 'buyer' ? 'Buyer' : activeRole === 'supplier' ? 'Supplier' : 'Broker'}`}
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-5">
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">
                    {lang === 'ar' ? 'البريد الإلكتروني' : 'Email Address'}
                  </label>
                  <div className="relative">
                    <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                      type="email"
                      placeholder="your.email@company.com"
                      className="input-base w-full pl-9 pr-3 py-2.5 text-sm"
                      {...register('email', {
                        required: lang === 'ar' ? 'البريد الإلكتروني مطلوب' : 'Email is required',
                        pattern: {
                          value: /^\S+@\S+\.\S+$/,
                          message: lang === 'ar' ? 'بريد إلكتروني غير صالح' : 'Invalid email address',
                        },
                      })}
                    />
                  </div>
                  {errors.email && (
                    <p className="flex items-center gap-1.5 text-xs text-red-400 mt-1.5">
                      <AlertCircle size={12} />
                      {errors.email.message as string}
                    </p>
                  )}
                </div>

                {/* Password */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="block text-sm font-medium text-foreground">
                      {lang === 'ar' ? 'كلمة المرور' : 'Password'}
                    </label>
                    <Link href="/forgot-password" className="text-xs text-primary hover:text-primary/80 transition-colors">
                      {lang === 'ar' ? 'نسيت كلمة المرور؟' : 'Forgot password?'}
                    </Link>
                  </div>
                  <div className="relative">
                    <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      placeholder={lang === 'ar' ? 'أدخل كلمة المرور' : 'Enter your password'}
                      className="input-base w-full pl-9 pr-10 py-2.5 text-sm"
                      {...register('password', {
                        required: lang === 'ar' ? 'كلمة المرور مطلوبة' : 'Password is required',
                      })}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(v => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="flex items-center gap-1.5 text-xs text-red-400 mt-1.5">
                      <AlertCircle size={12} />
                      {errors.password.message as string}
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary w-full py-2.5 text-sm flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <><Loader2 size={16} className="animate-spin" />{lang === 'ar' ? 'جارٍ تسجيل الدخول...' : 'Signing in...'}</>
                  ) : (
                    <>{lang === 'ar' ? 'تسجيل الدخول' : 'Sign In'}<ArrowRight size={16} /></>
                  )}
                </button>
              </form>

              {/* Demo credentials */}
              <div className="rounded-lg border border-dashed border-primary/40 bg-primary/5 p-3">
                <p className="text-xs font-semibold text-primary mb-2 flex items-center gap-1.5">
                  🎯 {lang === 'ar' ? 'حسابات العرض التجريبي' : 'Demo Accounts — Quick Fill'}
                </p>
                <div className="flex gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={() => {
                      setActiveRole('buyer');
                      setValue('email', seedEmails.buyer);
                      if (seedPasswords.buyer) setValue('password', seedPasswords.buyer);
                    }}
                    className="text-xs px-2.5 py-1 rounded bg-blue-500/15 text-blue-400 border border-blue-500/30 hover:bg-blue-500/25 transition-colors"
                  >
                    {lang === 'ar' ? '🛒 ملء المشتري' : '🛒 Fill Buyer'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setActiveRole('supplier');
                      setValue('email', seedEmails.supplier);
                      if (seedPasswords.supplier) setValue('password', seedPasswords.supplier);
                    }}
                    className="text-xs px-2.5 py-1 rounded bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/25 transition-colors"
                  >
                    {lang === 'ar' ? '⛽ ملء المورد' : '⛽ Fill Supplier'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setActiveRole('broker');
                      setValue('email', seedEmails.broker);
                      if (seedPasswords.broker) setValue('password', seedPasswords.broker);
                    }}
                    className="text-xs px-2.5 py-1 rounded bg-amber-500/15 text-amber-400 border border-amber-500/30 hover:bg-amber-500/25 transition-colors"
                  >
                    {lang === 'ar' ? '💼 ملء الوسيط' : '💼 Fill Broker'}
                  </button>
                  <button
                    type="button"
                    onClick={handleSeedAccounts}
                    disabled={seedLoading}
                    className="text-xs px-2.5 py-1 rounded bg-green-500/15 text-green-400 border border-green-500/30 hover:bg-green-500/25 transition-colors flex items-center gap-1 disabled:opacity-50"
                  >
                    {seedLoading ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />}
                    {seedLoading ? 'Seeding...' : '🌱 Seed Accounts'}
                  </button>
                </div>
                {seedResult && (
                  <p className="text-xs mt-2 text-primary/80 whitespace-pre-line">{seedResult}</p>
                )}
                <p className="text-xs text-muted-foreground mt-1.5">
                  {lang === 'ar' ? 'انقر لملء بيانات الاعتماد ثم اضغط تسجيل الدخول' : 'Click to fill credentials, then press Sign In'}
                </p>
              </div>

              {/* Register link */}
              <div className="text-center text-sm text-muted-foreground">
                {lang === 'ar' ? 'جديد في NautEX؟' : 'New to NautEX?'}{' '}
                <Link href="/register" className="text-primary hover:text-primary/80 transition-colors font-medium">
                  {lang === 'ar' ? 'سجّل شركتك' : 'Register your company'}
                </Link>
              </div>
            </div>
          </div>

          {/* Security note */}
          <p className="text-center text-xs text-muted-foreground mt-4">
            {lang === 'ar' ?'يتم تسجيل جميع أحداث تسجيل الدخول في سجل التدقيق.' :'All login events are recorded in the NautEX audit trail.'}
          </p>
        </div>
      </div>
    </div>
  );
}
